import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Typography, Paper } from '@mui/material';
import { useCookieConsent } from './useCookieConsent';
/**
 * Cookie consent banner component
 *
 * Displays a fixed banner at the bottom of the viewport when no consent decision exists.
 * Provides Accept and Reject buttons that persist the user's choice to localStorage.
 *
 * The banner automatically hides once a decision is made.
 *
 * @example
 * <CookieBanner />
 *
 * @example
 * // With callbacks
 * <CookieBanner
 *   onAccept={() => loadAnalytics()}
 *   onReject={() => console.log('User rejected')}
 * />
 */
export function CookieBanner({ onAccept, onReject }) {
    const { hasDecision, acceptConsent, rejectConsent } = useCookieConsent();
    // Don't render if user has already made a decision
    if (hasDecision) {
        return null;
    }
    const handleAccept = () => {
        acceptConsent();
        onAccept?.();
    };
    const handleReject = () => {
        rejectConsent();
        onReject?.();
    };
    return (_jsxs(Paper, { elevation: 8, sx: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            padding: { xs: '16px', sm: '20px 24px' },
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2
        }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(Typography, { variant: "body1", sx: {
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.6,
                        color: '#333'
                    }, children: "We use cookies to improve your experience and analyze site usage. By clicking \"Accept\", you consent to our use of analytics cookies. You can reject non-essential cookies by clicking \"Reject\"." }) }), _jsxs(Box, { sx: {
                    display: 'flex',
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexShrink: 0
                }, children: [_jsx(Button, { variant: "outlined", onClick: handleReject, sx: {
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                            borderColor: '#2c5aa0',
                            color: '#2c5aa0',
                            minWidth: { xs: '100%', sm: '120px' },
                            '&:hover': {
                                borderColor: '#1e3f7a',
                                backgroundColor: 'rgba(44, 90, 160, 0.04)'
                            }
                        }, children: "Reject" }), _jsx(Button, { variant: "contained", onClick: handleAccept, sx: {
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                            backgroundColor: '#2c5aa0',
                            color: '#ffffff',
                            minWidth: { xs: '100%', sm: '120px' },
                            '&:hover': {
                                backgroundColor: '#1e3f7a'
                            }
                        }, children: "Accept" })] })] }));
}
export default CookieBanner;
